import { encryptPassword } from '../utils/crypto';

let API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002/api';
if (window.location.protocol === 'https:' && API_BASE_URL.startsWith('http://')) {
  API_BASE_URL = API_BASE_URL.replace('http://', 'https://');
}

const api = {
  login: async (userType, email, password) => {
    const encryptedPassword = encryptPassword(password);
    const loginPath = userType === 'sub_agency' ? 'sub-agency' : userType;
    const response = await fetch(`${API_BASE_URL}/auth/${loginPath}/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password: encryptedPassword }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Login failed');
    }

    return response.json();
  },

  forgotPassword: async (userType, email) => {
    const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, user_type: userType }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.message || 'Failed to send reset OTP');
    }
    return data;
  },

  verifyResetOtp: async (userType, email, otp) => {
    const response = await fetch(`${API_BASE_URL}/auth/verify-reset-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, user_type: userType, otp }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.message || 'Invalid or expired OTP');
    }
    return data;
  },

  resetPassword: async (resetToken, password) => {
    const encryptedPassword = encryptPassword(password);
    const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${resetToken}`,
      },
      body: JSON.stringify({ password: encryptedPassword }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.message || 'Failed to reset password');
    }
    return data;
  },

  post: async (endpoint, data) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Request failed');
    }

    return response.json();
  },

  setAuthToken: (token) => {
    localStorage.setItem('authToken', token);
  },

  getAuthToken: () => {
    return localStorage.getItem('authToken');
  },

  logout: async () => {
    const token = localStorage.getItem('authToken');
    if (token) {
      await fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
    }
    localStorage.removeItem('authToken');
  },

  getStandards: async () => {
    const response = await fetch(`${API_BASE_URL}/academic/standards`);
    return response.json();
  },

  getDivisions: async () => {
    const response = await fetch(`${API_BASE_URL}/academic/divisions`);
    return response.json();
  },

  getRollNumbers: async () => {
    const response = await fetch(`${API_BASE_URL}/academic/roll-numbers`);
    return response.json();
  },

  getChildren: async () => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/profile/children`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response.json();
  },

  updateChildProfile: async (childId, profileData) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/profile/children/${childId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(profileData),
    });
    return response.json();
  },

  getSubscriptions: async () => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/subscription/my-subscriptions`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response.json();
  },

  getSubscriptionPlans: async () => {
    const response = await fetch(`${API_BASE_URL}/subscription/plans`);
    return response.json();
  },

  generateInvoice: async (subscriptionId) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/payment/generate-invoice`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ subscription_id: subscriptionId }),
    });
    return response.json();
  },

  createTransaction: async (transactionData) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/payment/create-transaction`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(transactionData),
    });
    return response.json();
  },

  updatePaymentStatus: async (transactionId, statusData) => {
    const response = await fetch(`${API_BASE_URL}/payment/update-status/${transactionId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(statusData),
    });
    return response.json();
  },

  createSubscription: async (subscriptionData) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/subscription/subscribe`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(subscriptionData),
    });
    return response.json();
  },

  renewSubscription: async (subscriptionId, planId) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/subscription/renew/${subscriptionId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ plan_id: planId }),
    });
    return response.json();
  },

  downloadInvoice: async (subscriptionId) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/payment/invoices`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    const invoices = await response.json();
    // Find invoice for this subscription and trigger download
    const invoice = invoices.find(inv => inv.subscription_id === subscriptionId);
    if (invoice) {
      // Create a simple invoice download (you can enhance this)
      const invoiceData = `Invoice: ${invoice.invoice_number}\nAmount: ₹${invoice.total_amount}\nDate: ${invoice.invoice_date}`;
      const blob = new Blob([invoiceData], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoice.invoice_number}.txt`;
      a.click();
      window.URL.revokeObjectURL(url);
    }
  },

  removeAuthToken: () => {
    localStorage.removeItem('authToken');
  },

  getLastLocation: async (deviceId) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/location/${deviceId}/last`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response.json();
  },

  getLocationHistory: async (deviceId, startDateTime, endDateTime) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/location/${deviceId}/history?start=${encodeURIComponent(startDateTime)}&end=${encodeURIComponent(endDateTime)}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response.json();
  },

  createStudentSubscription: async (subscriptionData) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/subscription/create-student-subscription`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(subscriptionData),
    });
    return response.json();
  },

  recordPayment: async (paymentData) => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/subscription/record-payment`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(paymentData),
    });
    return response.json();
  }
};

export default api;